import React from 'react';
import { useThree } from '@react-three/fiber';
import { Vector3 } from 'three';
import gsap from 'gsap';
import * as THREE from 'three';
// import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { OrbitControls as DreiOrbitControls } from '@react-three/drei';

interface ViewMode {
  position: [number, number, number];
  target: [number, number, number];
  up: [number, number, number];
  label: string;
}

const viewModes: Record<string, ViewMode> = {
  front: { 
    position: [0, 100, 0], 
    target: [0, 0, 0],
    up: [0, 0, -1],
    label: 'Front'
  },
  top: { 
    position: [0, 0, 100], 
    target: [0, 0, 0],
    up: [0, 1, 0],
    label: 'Top'
  },
  side: { 
    position: [100, 0, 0], 
    target: [0, 0, 0],
    up: [0, 1, 0],
    label: 'Side'
  },
  isometric: { 
    position: [57.735, 57.735, 57.735], // 100/√3 for equal distance in all directions
    target: [0, 0, 0],
    up: [0, 1, 0],
    label: 'Isometric'
  }
};

interface SceneControlsProps {
  currentView: string;
  isGridVisible: boolean;
  onViewChange: (view: string) => void;
  onGridToggle: (visible: boolean) => void;
}

export const SceneControls: React.FC<SceneControlsProps> = ({
  currentView,
  isGridVisible,
  onViewChange,
  onGridToggle
}) => {
  const { camera, controls } = useThree();

  const setView = (viewName: keyof typeof viewModes) => {
    const view = viewModes[viewName];
    
    // Ensure camera is perspective camera
    if (camera instanceof THREE.PerspectiveCamera) {
      gsap.to(camera.position, {
        duration: 1,
        x: view.position[0],
        y: view.position[1],
        z: view.position[2],
        ease: 'power2.inOut',
        onUpdate: () => {
          camera.lookAt(new THREE.Vector3(...view.target));
          camera.up.set(...view.up);
          camera.updateProjectionMatrix();
        }
      });
      
      if (controls) {
        gsap.to(controls.target, {
          duration: 1,
          x: view.target[0],
          y: view.target[1],
          z: view.target[2],
          ease: 'power2.inOut',
          onUpdate: () => controls.update()
        });
      }
    }

    onViewChange(viewName);
  };

  return null; // This component only handles camera controls, no visual rendering
};

// UI Controls Component (outside Canvas)
export const SceneControlsUI: React.FC<{
  currentView: string;
  isGridVisible: boolean;
  onViewChange: (view: string) => void;
  onGridToggle: (visible: boolean) => void;
}> = ({ currentView, isGridVisible, onViewChange, onGridToggle }) => {
  return (
    <div className="flex items-center space-x-4">
      <div className="flex space-x-2">
        {Object.entries(viewModes).map(([viewName, view]) => (
          <button
            key={viewName}
            className={`px-3 py-1 text-sm rounded-md transition-colors
                      ${currentView === viewName 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200'}`}
            onClick={() => onViewChange(viewName)}
          >
            {view.label}
          </button>
        ))}
      </div>
      <button
        className={`px-3 py-1 text-sm rounded-md transition-colors flex items-center space-x-2
                  ${isGridVisible 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200'}`}
        onClick={() => onGridToggle(!isGridVisible)}
      >
        <span>{isGridVisible ? 'Hide Grid' : 'Show Grid'}</span>
      </button>
    </div>
  );
}; 